import React, { useEffect, useRef, useState } from 'react'

const ASSETS = {
  light: {
    sources: [
      {
        src: '/videos/LOG25Anos-fundo-transparente.webm',
        type: 'video/webm',
      },
      { src: '/videos/log-25-on-light.mp4', type: 'video/mp4' },
    ],
    poster: '/videos/log-25-on-light.png',
  },
  dark: {
    sources: [{ src: '/videos/log-25-on-dark.mp4', type: 'video/mp4' }],
    poster: '/videos/log-25-on-dark.png',
  },
}

const scheduleIdle = fn => {
  if (typeof requestIdleCallback === 'function') {
    const id = requestIdleCallback(fn, { timeout: 4000 })
    return () => cancelIdleCallback(id)
  }
  const t = setTimeout(fn, 2000)
  return () => clearTimeout(t)
}

/** Temporary 25-year animated mark. Remove with USE_ANNIVERSARY_LOGO. */
const HeaderLogoAnniversary = ({ onDark = false, size = 136 }) => {
  const [reduceMotion, setReduceMotion] = useState(false)
  const [allowVideo, setAllowVideo] = useState(false)
  const [inView, setInView] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const wrapRef = useRef(null)
  const videoRef = useRef(null)
  const assets = onDark ? ASSETS.dark : ASSETS.light
  const showVideo = allowVideo && inView && !reduceMotion

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const sync = () => setReduceMotion(Boolean(mq.matches))
    sync()
    if (mq.addEventListener) mq.addEventListener('change', sync)
    else mq.addListener(sync)
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', sync)
      else mq.removeListener(sync)
    }
  }, [])

  useEffect(() => {
    const wrap = wrapRef.current
    if (!wrap || typeof IntersectionObserver === 'undefined') return undefined
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.01 }
    )
    io.observe(wrap)
    return () => io.disconnect()
  }, [])

  useEffect(() => {
    if (reduceMotion) return undefined
    let cancelIdle = () => {}
    const arm = () => {
      cancelIdle = scheduleIdle(() => setAllowVideo(true))
    }
    if (document.readyState === 'complete') arm()
    else window.addEventListener('load', arm, { once: true })
    return () => {
      window.removeEventListener('load', arm)
      cancelIdle()
    }
  }, [reduceMotion])

  useEffect(() => {
    if (!showVideo) {
      setIsPlaying(false)
      return undefined
    }
    const video = videoRef.current
    if (!video) return undefined

    const play = () => {
      if (document.hidden) {
        video.pause()
        return
      }
      if (!video.querySelector('source')) {
        assets.sources.forEach(({ src, type }) => {
          const el = document.createElement('source')
          el.src = src
          el.type = type
          video.appendChild(el)
        })
        video.load()
      }
      const playing = video.play()
      if (playing && typeof playing.catch === 'function') playing.catch(() => {})
    }

    const onPlaying = () => setIsPlaying(true)
    const onPause = () => setIsPlaying(false)
    video.addEventListener('playing', onPlaying)
    video.addEventListener('pause', onPause)
    document.addEventListener('visibilitychange', play)
    play()

    return () => {
      document.removeEventListener('visibilitychange', play)
      video.removeEventListener('playing', onPlaying)
      video.removeEventListener('pause', onPause)
      video.pause()
    }
  }, [showVideo, assets.sources])

  return (
    <span
      ref={wrapRef}
      className={
        'header-logo-wrap' + (onDark ? ' header-logo-wrap--on-dark' : '')
      }
    >
      <img
        className={
          'header-logo header-logo--poster' + (isPlaying ? ' is-hidden' : '')
        }
        src={assets.poster}
        width={size}
        height={size}
        alt="LOG"
        decoding="async"
      />
      {showVideo ? (
        <video
          key={assets.poster}
          ref={videoRef}
          className="header-logo header-logo--video"
          width={size}
          height={size}
          muted
          loop
          playsInline
          preload="none"
          aria-hidden="true"
        />
      ) : null}
    </span>
  )
}

export default HeaderLogoAnniversary
